/* OsCatalogGridViewRenderer.js */

"use strict";

const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");
const _ = require("lodash");

class OsCatalogGridViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("os-catalog-grid-view-gl");
        this._logger = this._model.logger;
        this._defaultViewActive = false;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "FeedInitialized",
            (this._onFeedInitialized = this._onFeedInitialized.bind(this))
        );
        // this._model.gameEditor.on('ColumnSettingsChange', this._onColumnSettingsChanged = this._onColumnSettingsChanged.bind(this));
        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._osCatalogEntityGrid.on(
            "UserSelectedRows",
            (this._onGridUserSelectedRows = this._onGridUserSelectedRows.bind(this))
        );
        this._osCatalogEntityGrid.on(
            "UserRowDoubleClicked",
            (this._onUserDblClickedRow = this._onUserDblClickedRow.bind(this))
        );
        this._osCatalogEntityGrid.on(
            "UserSorted",
            (this._onUserSorted = this._onUserSorted.bind(this))
        );
        this._osCatalogEntityGrid.on(
            "GridBusyStart",
            (this._onGridBusyStart = this._onGridBusyStart.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._model) {
            this._model.removeListener("FeedInitialized", this._onFeedInitialized);
            if (this._model.pagingFeed) {
                this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
            }
        }
        if (this._osCatalogEntityGrid) {
            this._unregisterGridListeners();
        }
    }

    _unregisterGridListeners() {
        this._osCatalogEntityGrid.removeAllListeners("UserSelectedRows");
        this._osCatalogEntityGrid.removeAllListeners("UserRowDoubleClicked");
        this._osCatalogEntityGrid.removeListener("UserSorted", this._onUserSorted);
        this._osCatalogEntityGrid.removeListener("GridBusyStart", this._onGridBusyStart);
    }

    _renderAvailableActions() {
        this._model.gameEditor.disableAllActions();

        Promise.all([this.model.areCatalogsEnabled(), this.model.isUserCanCreateCatalog()]).then(
            ([enabledCatalogs, canCreate]) => {
                if (!enabledCatalogs) {
                    return;
                }

                let numSelected = this.selectedRows.length || 0;
                if (canCreate) {
                    this._model.gameEditor.enableAction(
                        Registrations.CATALOG_EXPLORER_ACTION_CREATE
                    );
                }

                if (canCreate && numSelected === 1) {
                    this._model.gameEditor.enableAction(
                        Registrations.CATALOG_EXPLORER_ACTION_CLONE
                    );
                }

                this._model.gameEditor.enableAction(Registrations.CATALOG_EXPLORER_ACTION_EDIT);
                this._model.gameEditor.enableAction(Registrations.CATALOG_EXPLORER_ACTION_DELETE);
                this._model.gameEditor.enableAction(Registrations.CATALOG_EXPLORER_ACTION_EXPORT);
            }
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [_.invoke(this._osCatalogEntityGrid, "shutdown") || Promise.resolve()];

        return Promise.all(promArr).then(
            () => {
                this._osCatalogEntityGrid = undefined;
                this._defaultViewActive = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn(err, "OsCatalogGridViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model.whenModelIsReady().then(() => {
            this._updatePagingFeedUserColumns();
            this._osCatalogEntityGrid = new EntityGrid({
                gridKey: this.model._gridKey,
                logger: this._logger,
                singleSelect: false,
                selectedRows: _.get(this._model.getSelectedCatalogIds(), "selectedRows", []),
                columns: this._model.columnConfig,
                pagingFeed: this._model.pagingFeed,
                showHeader: true,
                showLinesNumber: this._model._gameEditor.renderer._rootElem.querySelector(
                    "game-editor-view-actions-gl left-container"
                ),
                columnSettingsWidgetContainer:
                    this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                filterComponent:
                    this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                advancedFilterComponent:
                    this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                modalUserFeedback: this._model._modalUserFeedback,
                userGridData: this._model._userGridData,
            });

            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._osCatalogEntityGrid.renderer.rootElement);
            }
            this._renderAvailableActions();
            this._onFeedRefreshView();
            this._registerListeners();

            if (_.get(this._model._gameEditor._openEditorFromUrl, "open", false)) {
                _.get(this._model._gameEditor._openEditorFromUrl, "selectedIds", []).forEach(
                    (catalogid) => {
                        this._openEditor(catalogid);
                    }
                );
            }
        });
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    get selectedRows() {
        return this._osCatalogEntityGrid.getSelectedRows();
    }

    restartGrid() {
        //this._unregisterGridListeners();
        return this._osCatalogEntityGrid.shutdown().then(() => {
            this._instantiateGrid();
            this._rootElem.appendChild(this._osCatalogEntityGrid.renderer.rootElement);
            this._registerGridListeners();
            this._renderAvailableActions();
        });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _updatePagingFeedUserColumns() {
        this._model.pagingFeed._userColumns.forEach((col) => {
            Object.assign(col, this._model.columnConfig[col.name]);
        });
    }

    _showDefaultMessageDisplay() {
        this.model.isUserCanCreateCatalog().then((isAuthorized) => {
            let message = {};
            if (!isAuthorized) {
                message = {
                    message: "Create entry",
                    icon: "osmanager.add_os_catalog",
                };
            } else {
                message = {
                    message: "Create entry",
                    icon: "osmanager.add_os_catalog",
                    action: Registrations.CATALOG_EXPLORER_ACTION_CREATE,
                };
            }

            this._model.gameEditor
                .showDefaultMessageView(message)
                .then(() => {
                    this._defaultViewActive = true;
                })
                .catch((err) => {
                    this._logger.warn(
                        {error: err},
                        "PolicyListViewerRenderer._showDefaultMessageDisplay error"
                    );
                });
        });
    }

    _hideDefaultMessageDisplay() {
        return this._model.gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "PolicyListViewerRenderer._hideDefaultMessageDisplay error"
                );
            });
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onGridBusyStart() {
        if (this._model.pagingFeed.totalEntities > this._model.pagingFeed.pageSize * 2) {
            let gridBusyEnded = false;
            this._osCatalogEntityGrid.once("GridBusyEnd", () => {
                gridBusyEnded = true;
            });
            this._model._modalUserFeedback.displayLoading().then(() => {
                if (gridBusyEnded) {
                    this._model._modalUserFeedback.hideDisplayed();
                } else {
                    this._osCatalogEntityGrid.once("GridBusyEnd", () => {
                        this._model._modalUserFeedback.hideDisplayed();
                    });
                }
            });
        }
    }

    _onFeedInitialized() {
        // this._renderGridRows();
        this._renderAvailableActions();
    }

    _onUserSorted(eventData) {
        this._model.onUserSorted(eventData);
    }

    _onUserDblClickedRow(obj) {
        this._openEditor(obj.selectedRow);
    }

    _openEditor(selectedId) {
        this._model.openOsCatalogInstanceEditor(
            selectedId,
            Registrations.OS_CATALOG_EDITOR_VIEW_DETAILS
        );
    }

    _onGridUserSelectedRows(selectedRowIds) {
        this._model.updateExplorerState("selectedIds", selectedRowIds || []);
        this._renderAvailableActions();
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        const filterData = this._model.pagingFeed.getFilterData();

        if (totalEntities === 0 && _.isEmpty(filterData)) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    // TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = OsCatalogGridViewRenderer;
